import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  effect,
  inject,
  input,
  viewChild,
  computed,
  output,
  ChangeDetectionStrategy, OnInit, signal, ViewContainerRef, TemplateRef
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { IconsModule } from '@progress/kendo-angular-icons';
import { SplitterModule } from '@progress/kendo-angular-layout';
import { AiModelsComponent } from '../ai-models/ai-models.component';
import { IOpenAiModel } from '../../interfaces/i-open-ai-model';
import { AutomationStateService } from '../../pages/ai-chat/services/automation-state.service';
import { ChatDocumentService } from '../../pages/ai-chat/services/chat-document.service';
import { AttachmentDocComponent } from '../attachment-doc/attachment-doc.component';
import { ChatSessionService } from '../../pages/ai-chat/services/chat-session.service';
import { AiChatService } from '../../pages/ai-chat/services/ai-chat.service';
import { AiChatStreamingService } from '../../pages/ai-chat/services/ai-chat-streaming.service';
import { MultiSessionService } from '../../services/multi-session.service';
import { ChatRole } from '../../enums/api.enum';
import { ChatMessageService } from '../../pages/ai-chat/services/chat-message.service';
import { SpeechService } from '../../services/speech.service';
import SessionInputData from '../../models/session-input-data.model';
import { ExportChatSessionService } from '../../services/export-chat-session.service';
import { minusOutlineIcon, SVGIcon } from "@progress/kendo-svg-icons";
import { ApiService } from '../../services/api.service';
import { AlertEnum } from '../alert/alert.enum';
import { AlertService } from '../alert/alert.service';
import { AutomationExecutionService } from "../../pages/ai-chat/services/automation-execution.service";
import { UtilitiesService } from '../../services/utilities.service';
import { ConversationStarterService } from '../../pages/ai-chat/services/conversation-starter.service';
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { Overlay, OverlayRef } from "@angular/cdk/overlay";
import { TemplatePortal } from "@angular/cdk/portal";
import { IGrantedAgent } from '../../interfaces/i-agents';
import { UiLayoutService } from '../../ui-layout/services/ui-layout.service';
import { SpinnerService } from '../spinner/spinner.service';

@Component({
  selector: 'app-ai-chat-prompt',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    IconsModule,
    ButtonsModule,
    SplitterModule,
    AiModelsComponent,
    AttachmentDocComponent,
  ],
  templateUrl: './ai-chat-prompt.component.html',
  styleUrl: './ai-chat-prompt.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AiChatPromptComponent implements OnInit {
  chatInput = viewChild.required<ElementRef>('chatInput');
  mentionListTemplate = viewChild<TemplateRef<any>>('mentionList');
  selectedModel: IOpenAiModel = { accountName: "", model: "" };

  aiChatPromptForm!: FormGroup;
  speechActive = false;
  promptText = input<string>('');
  promptAttachedDocuments = input<any[]>([]);
  showExportChatButton = input<boolean>(true);
  extraClasses = input<string | string[]>('');
  promptInputChanged = output<string>();
  promptSubmitted = output<void>();

  // Computed signal that tracks whether the currently selected session is actively streaming.
  isCurrentSessionSubmitting = computed(() =>
    this.chatSessionService.selectedSessionId()
      ? this.multiSessionService.isSessionStreaming(this.chatSessionService.selectedSessionId())
      : false
  );

  // Tracks whether a prompt submission from the Start Session page is in progress.
  // This is needed because isCurrentSessionSubmitting() is always false on that page
  // (no session ID exists yet), so without this guard multiple Enter presses would
  // dispatch duplicate requests before the session is created.
  #isStartConversationSubmitting = signal<boolean>(false);

  // Single source of truth for whether the chat input area and its controls should be disabled.
  // Use this in the template instead of repeating the individual conditions.
  isInputDisabled = computed(() =>
    this.multiSessionService.hasReachedMaxConcurrency() || this.isCurrentSessionSubmitting() || this.#isStartConversationSubmitting()
  );

  // Current query string for the '@' mention system.
  mentionQuery = signal<string>('');

  // Cached list of all granted agents fetched once
  #allAgents = signal<IGrantedAgent[]>([]);

  // The session ID used to populate #allAgents — used to detect stale cache on session switch
  #allAgentsFetchedForSessionId = signal<string | null>(null);

  // The session ID for which the mentions overlay was opened.
  // We use this to detect session switches and close the overlay when we switch sessions
  #overlayOpenedForSessionId = signal<string | null>(null);

  // Whether agents are currently being loaded
  #agentsLoading = signal<boolean>(false);

  // Agent IDs selected via the mention system (not from session)
  #mentionSelectedAgentIds = signal<string[]>([]);

   // Index of the currently focused item in the mention list for keyboard navigation
   #focusedMentionIndex = signal<number>(0);

   // Track message count to detect when a new user message is added (for promptSubmitted emission)
   #lastMessageCount = signal<number>(0);

   // Track the session ID to avoid false positives when switching between multi-sessions
   #lastTrackedSessionId = signal<string | number | null>(null);

   // Public computed for template access
  agentsLoading = computed(() => this.#agentsLoading());
  focusedMentionIndex = computed(() => this.#focusedMentionIndex());

  // Selected agents to display as chips (only those selected via mention system)
  selectedAgents = computed(() => {
    const selectedIds = this.#mentionSelectedAgentIds();
    const allAgents = this.#allAgents();

    if (selectedIds.length === 0 || allAgents.length === 0) {
      return [];
    }

    // Map selected agent IDs to full agent objects
    return selectedIds
      .map(agentId => allAgents.find(a => a.id === agentId))
      .filter((agent): agent is IGrantedAgent => agent !== undefined);
  });

  // Filtered list of agents based on the mention query
  filteredAgents = computed(() => {
    const query = this.mentionQuery().toLowerCase();
    const agents = this.#allAgents();

    if (!query) {
      return agents;
    }

    return agents.filter(agent =>
      agent.name.toLowerCase().includes(query)
    );
  });

  // Normalized class string for the container.  Use this to add extra classes to the container.  Any extra styling
  // may have to be applied in the _material-theme.scss file if it involves Angular Material components.
  readonly containerClass = computed(() => {
    const extra = this.extraClasses();
    const extraClassString = Array.isArray(extra) ? extra.join(' ') : extra;
    return `ai-chat-prompt-container ${extraClassString ?? ''}`.trim();
  });

  readonly minusOutlineIcon: SVGIcon = minusOutlineIcon;

  // Services
  chatDocumentService = inject(ChatDocumentService);
  chatSessionService = inject(ChatSessionService);
  automationStateService = inject(AutomationStateService);
  aiChatService = inject(AiChatService);
  exportChatSessionService = inject(ExportChatSessionService);
  aiChatStreamingService = inject(AiChatStreamingService);
  multiSessionService = inject(MultiSessionService);

  #formBuilder = inject(FormBuilder);
  #chatMessageService = inject(ChatMessageService);
  #speechService = inject(SpeechService);
  #alertService = inject(AlertService);
  #utilitiesService = inject(UtilitiesService);
  #uilayoutService = inject(UiLayoutService);
  // @ search config
  #viewContainerRef = inject(ViewContainerRef);
  #overlay = inject(Overlay);
  #overlayRef: OverlayRef | null = null;
  // END @ search config
  readonly #apiService = inject(ApiService);
  readonly conversationStarterService = inject(ConversationStarterService);
  readonly #automationExecutionService = inject(AutomationExecutionService);
  readonly #spinnerService = inject(SpinnerService);

  constructor() {
    this.aiChatPromptForm = this.#formBuilder.group({
      content: [{ value: '', disabled: this.isInputDisabled() }],
    });

    effect(() => {
      if (!this.conversationStarterService.isStartConversation()) {
        const sessionId = this.chatSessionService.selectedSessionId();

        // #B-6792  Bug 8 - Agent Mentions Overlay Persist Across Sessions and Create Inconsistent Agent Selection Behavior
        // This happens when the mentions overlay is open, then you switch a session, the overlay is still displayed.
        // Now if we switch sessions and the mentions overlay is opened, close the overlay.
        // #overlayOpenedForSessionId tracks exactly which session the overlay was opened for, independent of fetch state.
        if (this.#overlayRef && (this.#overlayOpenedForSessionId() !== String(sessionId))) {
            this.#closeMentionsOverlay();
        }

        if (this.conversationStarterService.isSessionCreatedFromStartConversation()) {
          if (this.chatDocumentService.selectedDocuments().length > 0) {
            // If we have selected documents, update the session input data documents
            this.chatSessionService.updateSessionInputDataDocuments(this.chatDocumentService.selectedDocuments());
          }

          this.conversationStarterService.isSessionCreatedFromStartConversation.set(false);
        }

        const sessionInputData =
          this.chatSessionService.getSessionInputData(sessionId);

        if (sessionInputData.text.length > 0) {
          this.aiChatPromptForm.get('content')?.setValue(sessionInputData.text);
        } else {
          this.aiChatPromptForm.get('content')?.setValue('');
        }

        if (sessionInputData.documents.length > 0) {
          this.chatDocumentService.selectedDocuments.set(sessionInputData.documents);
        } else {
          this.chatDocumentService.selectedDocuments.set([]);
        }

      if (this.isInputDisabled()) {
        this.aiChatPromptForm.get('content')?.disable();
        this.aiChatPromptForm.get('content')?.setValue('');
        // Don't clear documents when the session is paused waiting for file upload input,
        // as the user needs to attach documents during this workflow step.
        // if (!this.multiSessionService.isSessionWaitingForInput(sessionId!)) {
          // console.log('Clearing selected documents as session is submitting and not waiting for input');
          // console.log(`current doc length :: ${this.chatDocumentService.selectedDocuments()?.length}`);
          //this.chatDocumentService.selectedDocuments.set([]);
        // }

          const lastMessage = this.#getLastMessage();

          // Only set isSubmitting to false if the last message is a system ERROR message
          // System messages from configuration updates or automation should not trigger this
          if (lastMessage?.senderId === 'system' && lastMessage?.error === true) {
            this.multiSessionService.errorSession(sessionId!);
          }
        } else {
          this.aiChatPromptForm.get('content')?.enable();
          // B-5970 - Processing keeps showing in chat session when there is no submission.
          // if we aren't submitting anymore, we should check for any stuck "In Progress" messages.
          // if we have any, we set them to the default "Error Processing" message.
          this.aiChatService.checkForStuckInProgressMessages();

          // NOTE: Commenting this code out to prevent focus stealing while typing in a dialog
          // this.chatInput()?.nativeElement.focus();
        }
    } else {
      // Start Session page — the session-specific block above is skipped, but we still need to
      // react to concurrency changes so the form control is enabled/disabled correctly.
      // Reading isInputDisabled() here ensures Angular tracks it as a dependency of this effect,
      // causing it to re-run whenever hasReachedMaxConcurrency() or isCurrentSessionSubmitting() changes.
      if (this.isInputDisabled()) {
        this.aiChatPromptForm.get('content')?.disable();
      } else {
        this.aiChatPromptForm.get('content')?.enable();
      }
    }
    });

    // Handle promptText input signal (for restoring canceled message text)
    effect(() => {
      const text = this.promptText();
      if (text && text.length > 0) {
        this.aiChatPromptForm.get('content')?.setValue(text);
      }
    });

    // Emit promptSubmitted when a new user message is added to the messages signal
    // and ensures scrolling happens after the message actually exists in ChatMessageService.
    // Only emits when a message is added to the CURRENT session to avoid false positives
    // when switching between concurrent sessions.
    effect(() => {
      const messages = this.#chatMessageService.messages();
      const currentSessionId = this.chatSessionService.selectedSessionId();
      const currentMessageCount = messages?.length ?? 0;
      const lastCount = this.#lastMessageCount();
      const lastSessionId = this.#lastTrackedSessionId();

      // Only emit if:
      // 1. Still in the same session AND
      // 2. Message count increased (new message was added)
      if (currentSessionId === lastSessionId && currentMessageCount > lastCount) {
        // Check if the most recent message is a user message
        const lastMessage = messages?.[currentMessageCount - 1];
        if (lastMessage?.role === ChatRole.User) {
          // Emit the event so parent can scroll
          this.promptSubmitted.emit();
        }
      }

      // Update tracking for next comparison
      this.#lastTrackedSessionId.set(currentSessionId);
      this.#lastMessageCount.set(currentMessageCount);
    });
  }

  ngOnInit(): void {
    // TODO ask Rick about this method before removing it.
    // this.#resetPromptState();
  }

  onPromptInput(event: Event): void {

    this.adjustTextareaHeight();
    const value = (event.target as HTMLTextAreaElement).value ?? '';
    this.promptInputChanged.emit(value);

    const textarea = this.chatInput()?.nativeElement;
    if (!textarea) return;

    if (!this.conversationStarterService.isStartConversation()) {
      const cursorIndex = textarea.selectionStart;
      const textBeforeCursor = textarea.value.slice(0, cursorIndex);

      // Matches '@' followed by word characters at the end of the string
      const match = textBeforeCursor.match(/@(\w*)$/);

      if (match) {
        this.mentionQuery.set(match[1]);

        // Fetch agents if not yet loaded, or if the session has changed since the last fetch
        const currentSessionId = String(this.chatSessionService.selectedSessionId());
        if (!this.#agentsLoading() && (this.#allAgentsFetchedForSessionId() !== currentSessionId || this.#allAgents().length === 0)) {
          this.#fetchGrantedAgents();
        }

        // Only show if it's not already open
        if (!this.#overlayRef) {
          this.#showMentionsOverlay();
          this.#focusedMentionIndex.set(0); // Reset focus to first item
        }
      } else {
        this.mentionQuery.set('');
        this.#closeMentionsOverlay();
      }
      // Update session input data immediately to trigger draft icon update
      this.updateSessionInputDataWithTextAndAttachedDocuments();
    }

  }

  /**
   * Handles keyboard navigation within the mention list overlay.
   * Supports Arrow Down, Arrow Up, Enter, Escape, and Tab keys.
   * @param event The keyboard event from the textarea
   */
  onTextareaKeydown(event: KeyboardEvent): void {
    // Only handle keyboard events if the mention overlay is open
    if (!this.#overlayRef) return;

    const agents = this.filteredAgents();
    if (agents.length === 0) return;

    const currentIndex = this.#focusedMentionIndex();

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        event.stopPropagation();
        this.#focusedMentionIndex.set(Math.min(currentIndex + 1, agents.length - 1));
        this.#scrollToFocusedItem();
        break;

      case 'ArrowUp':
        event.preventDefault();
        event.stopPropagation();
        this.#focusedMentionIndex.set(Math.max(currentIndex - 1, 0));
        this.#scrollToFocusedItem();
        break;

      case 'Enter':
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        if (agents[currentIndex]) {
          this.selectMention(agents[currentIndex].name);
        }
        break;

      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        this.#closeMentionsOverlay();
        this.mentionQuery.set('');
        break;

      case 'Tab':
        event.preventDefault();
        event.stopPropagation();
        if (agents[currentIndex]) {
          this.selectMention(agents[currentIndex].name);
        }
        break;
    }
  }

  async executePrompt(): Promise<void> {
    // Redundancy check to prevent sending a message while max concurrency limit has been reached,
    // but it shouldn't get this to point due to disabling the chat input area.
    if (this.multiSessionService.hasReachedMaxConcurrency()) {
      this.multiSessionService.showMaxConcurrencyAlert();
      return;
    }

    // Prevent duplicate submissions while the start-session request is already in flight.
    if (this.#isStartConversationSubmitting()) {
      return;
    }

    if (!this.conversationStarterService.isStartConversation()) {
      this.sendMessage();
    } else {
      const prompt = this.aiChatPromptForm.get('content')?.value.trim();

      // TODO: Handle null properly.
      const model = this.conversationStarterService.conversation()!.model!;

      const isGpt5 = this.#utilitiesService.isGpt5Model(model.model);
      let temperature: number = 0.5;
      let topP: number = 0.5;

      if (isGpt5) {
        temperature = 1.0;
        topP = 1.0;
      }

      if (!prompt || prompt.trim().length === 0) {
        return;
      }

      // Disable input and show the global spinner immediately so the user cannot
      // submit again while the session is being created.
      this.#isStartConversationSubmitting.set(true);
      this.#spinnerService.show();
      try {
        this.conversationStarterService.conversation.update((item) => {
          if (!item) {
            return item;
          }
          return {
            ...item,
            prompt,
            model,
            temperature,
            topP
          };
        });
        await this.#automationExecutionService.generateChatSessionConfiguration();
        setTimeout(() => this.sendMessage(ChatRole.User, prompt), 1000);
      } finally {
        this.#isStartConversationSubmitting.set(false);
        this.#spinnerService.hide();
      }
    }
  }

  async sendMessage(chatRole?: ChatRole, prompt?: string): Promise<void> {
    this.updateSessionInputDataWithTextAndAttachedDocuments();

    const content = prompt ?? this.aiChatPromptForm.get('content')?.value;

    const sessionId = this.chatSessionService.selectedSessionId();
    const sessionInputData =
      this.chatSessionService.getSessionInputData(sessionId);
    const navItems = this.#uilayoutService.navItems();

    this.#uilayoutService.updateAndSortChatSessionsInLeftNav(String(sessionId), navItems, true);

    if (sessionInputData.instruction) {
      this.automationStateService.instructionInProgress.set(
        sessionInputData.instruction
      );
    }

    if (content) {
      setTimeout(() => this.aiChatStreamingService.sendMessageWithStreaming(content, chatRole, sessionId), 1000);
      if (this.conversationStarterService.isStartConversation()) {
        this.conversationStarterService.isStartConversation.set(false);
      }
      this.aiChatPromptForm.reset();

      // Clear selected agents after sending message
      this.#mentionSelectedAgentIds.set([]);
      // Note: promptSubmitted is now emitted reactively via an effect
      // that watches ChatMessageService.messages() for new user messages,
      // ensuring the scroll happens after the message actually exists.
    }
  }

  async submitOnEnter(event: Event): Promise<void> {
    const keyboardEvent = event as KeyboardEvent;

    // If the mention overlay is open, let onTextareaKeydown handle the Enter key
    if (this.#overlayRef) {
      return;
    }

    keyboardEvent.preventDefault();
    if (!this.conversationStarterService.hasError()) {
      await this.executePrompt();
    }
  }

  clear(): void {
    this.aiChatPromptForm.get('content')?.setValue('');

    if (!this.conversationStarterService.isStartConversation()) {
      const sessionId = this.chatSessionService.selectedSessionId();
      if (sessionId) {
        const currentInputData =
          this.chatSessionService.getSessionInputData(sessionId);
        this.chatSessionService.setSessionInputData(sessionId, {
          text: '',
          documents: currentInputData.documents,
          instruction: undefined,
        });
      }
    }

    this.automationStateService.instructionInProgress.set(null);
    this.automationStateService.workflowBeingExecuted.set(null);
  }

  /**
   * Removes an attachment from the selected documents for the chat session.
   */
  removeAttachment(index: number) {
    const selectedDocuments = this.chatDocumentService.selectedDocuments();
    selectedDocuments.splice(index, 1);
    this.chatDocumentService.selectedDocuments.set(selectedDocuments);
    // Update session input data immediately to trigger draft icon update
    this.updateSessionInputDataWithTextAndAttachedDocuments();
  }

  /**
   * Removes an agent from the current session's agents array.
   * @param agent The agent to remove from the session.
   */
  removeAgent(agent: IGrantedAgent): void {
    // Remove from mention-selected agents list
    const currentMentionSelected = this.#mentionSelectedAgentIds();
    this.#mentionSelectedAgentIds.set(currentMentionSelected.filter(id => id !== agent.id));

    const session = this.chatSessionService.selectedSession();
    if (!session) return;

    // Create updated session with the agent removed
    const updatedSession = {
      ...session,
      agents: session.agents.filter(id => id !== agent.id)
    };

    // Update in the chat sessions array
    this.chatSessionService.chatSessions.update((sessions) => {
      if (!sessions) return sessions;
      return sessions.map((s) => (s.id === session.id ? updatedSession : s));
    });

    // Persist the session to the backend
    this.#apiService.persistSession(updatedSession).subscribe({
      error: (error) => {
        console.error('Failed to remove agent from session:', error);
        this.#alertService.showAlert({
          message: `Failed to remove agent "${agent.name}" from session.`,
          type: AlertEnum.error,
        });
      }
    });
  }

  updateSessionInputDataWithTextAndAttachedDocuments(): void {
    const sessionId = this.chatSessionService.selectedSessionId();
    if (sessionId && !this.conversationStarterService.isStartConversation()) {
      const inputText = this.aiChatPromptForm.get('content')?.value ?? '';
      const attachedDocuments = this.chatDocumentService.selectedDocuments();

      const currentInputData =
        this.chatSessionService.getSessionInputData(sessionId);

      const inputData: SessionInputData = {
        text: inputText,
        documents: attachedDocuments,
        instruction: currentInputData.instruction,
      };

      this.chatSessionService.setSessionInputData(sessionId, inputData);
    }
  }

  async speechInput(): Promise<void> {
    this.aiChatPromptForm.get('content')?.disable();
    this.speechActive = true;

    const chatFormContent: string =
      this.aiChatPromptForm.get('content')?.value != null
        ? this.aiChatPromptForm.get('content')?.value
        : '';
    const prompt = await this.#speechService.listen(navigator.language);
    const newInputText =
      chatFormContent && chatFormContent.length > 0
        ? `${chatFormContent} ${prompt}`
        : `${prompt}`;
    this.aiChatPromptForm.get('content')?.setValue(newInputText);
    this.aiChatPromptForm.get('content')?.enable();

    this.updateSessionInputDataWithTextAndAttachedDocuments();

    this.speechActive = false;
  }

  /**
   * Cancels the currently in-progress prompt request by aborting the active stream.
   * `cancelStreamingMessage` is self-contained: it aborts the HTTP request,
   * cleans up messages, restores the user's input, and persists the session.
   */
  async cancelRequest(): Promise<void> {
    await this.aiChatStreamingService.cancelStreamingMessage();
  }

  exportChat(): void {
    const messages = this.chatSessionService.selectedSession()?.messages;
    const combinedMarkdown = (messages ?? [])
      .filter(
        (msg) => msg.clipboardVersion && msg.clipboardVersion.trim() !== ''
      )
      .map((msg) => `**${msg.role}**\n${msg.clipboardVersion}`)
      .join('\n\n');

    this.exportChatSessionService.convertAndDownload(
      combinedMarkdown,
      'export-chat-session-md.docx'
    );
  }

  /**
   * Finalizes the mention selection by injecting the chosen name into the chat input.
   * * * Workflow:
   * 1. **String Manipulation**: Extracts the text before and after the cursor.
   * It uses a regular expression to find the most recent '@' mention trigger and
   * replaces it with the full name provided.
   * 2. **Form Update**: Programmatically updates the Angular Reactive Form control
   * with the newly constructed string (including a trailing space for convenience).
   * 3. **Session Update**: Adds the selected agent's ID to the current session's agents array.
   * 4. **Cleanup**: Resets the `mentionQuery` signal and disposes of the overlay.
   * 5. **UX Restoration**: Re-focuses the textarea and triggers a height adjustment
   * to ensure the UI remains consistent after the text insertion.
   * * @param {string} name - The name of the wizard or entity selected from the mention list.
   * @returns {void}
   */
  selectMention(name: string): void {
    const textarea = this.chatInput()?.nativeElement;
    if (!textarea) return;

    const value = textarea.value;
    const cursorIndex = textarea.selectionStart;
    const textBeforeCursor = value.slice(0, cursorIndex);
    const textAfterCursor = value.slice(cursorIndex);

    // Replaces the mention trigger with the full name
    const updatedTextBefore = textBeforeCursor.replace(/@\w*$/, `@${name} `);
    const newValue = updatedTextBefore + textAfterCursor;

    // Calculate the new cursor position (right after the inserted name and space)
    const newCursorPosition = updatedTextBefore.length;

    // Update the Form Control directly
    this.aiChatPromptForm.get('content')?.setValue(newValue);

    // Add the selected agent to the session
    this.#addAgentToSession(name);

    // Clean up
    this.mentionQuery.set('');
    this.#closeMentionsOverlay();

    // Refocus the textarea and set cursor position
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPosition, newCursorPosition);
      this.adjustTextareaHeight();
    }, 0);
  }

  /**
   * Dynamically adjusts the height of the chat input textarea to match its content.
   * * This method implements an "auto-grow" behavior by:
   * 1. Temporarily resetting the height to 'auto' to calculate the true scroll height.
   * 2. Setting the height to the current `scrollHeight` in pixels.
   * * This ensures the textarea expands as the user types multi-line prompts and
   * shrinks when text is deleted, preventing unnecessary scrollbars within the input field.
   * * @returns {void}
   */
  adjustTextareaHeight(): void {
    const textarea = this.chatInput()?.nativeElement;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }

  #getLastMessage() {
    const messages = this.#chatMessageService.messages();
    const messageQueue = this.#chatMessageService.messageQueue()?.messageId;
    const message = messages?.find((m) => m.id === messageQueue);

    if (!message && messages && messages[messages.length - 1]) {
      return messages[messages.length - 1];
    }

    return message;
  }

  modelSelected(event: IOpenAiModel) {
    this.selectedModel = event;
    if (this.conversationStarterService.isStartConversation()) {
      this.conversationStarterService.conversation.update((item => item ? { ...item, model: event } : item));
    }
  }

  /**
   * Updates the temperature parameter for the current session when model is changed to GPT-5
   * @param value The new temperature value
   */
  updateTemperature(value: number): void {
    const session = this.chatSessionService.selectedSession();
    if (session) {
      const updatedSession = { ...session, temperature: value };

      // Update in the chat sessions array
      this.chatSessionService.chatSessions.update((sessions) => {
        if (!sessions) return sessions;
        return sessions.map((s) => (s.id === session.id ? updatedSession : s));
      });

      // Persist the session to the backend
      this.#apiService.persistSession(updatedSession).subscribe({
        error: (error) => {
          this.#alertService.showAlert({
            message: `Failed to update temperature.`,
            type: AlertEnum.error,
          });
        }
      });

      // Add system message about the change
      const updatedTempMessage = this.aiChatService.handleAiConfigMismatch(
        session,
        undefined,
        value,
        undefined
      );

      if (updatedTempMessage.length > 0) {
        this.aiChatService.createConfigChangeSystemMessage(
          session,
          updatedTempMessage,
          session.id
        );
      }
    }
  }

  /**
   * Updates the topP parameter for the current session when model is changed to GPT-5
   * @param value The new topP value
   */
  updateTopP(value: number): void {
    const session = this.chatSessionService.selectedSession();
    if (session) {
      const updatedSession = { ...session, topP: value };

      // Update in the chat sessions array
      this.chatSessionService.chatSessions.update((sessions) => {
        if (!sessions) return sessions;
        return sessions.map((s) => (s.id === session.id ? updatedSession : s));
      });

      // Persist the session to the backend
      this.#apiService.persistSession(updatedSession).subscribe({
        error: (error) => {
          this.#alertService.showAlert({
            message: `Failed to update topP.`,
            type: AlertEnum.error,
          });
        }
      });

      // Add system message about the change
      const updatedTopPMessage = this.aiChatService.handleAiConfigMismatch(
        session,
        undefined,
        undefined,
        value
      );

      if (updatedTopPMessage.length > 0) {
        this.aiChatService.createConfigChangeSystemMessage(
          session,
          updatedTopPMessage,
          session.id
        );
      }
    }
  }

  #resetPromptState(): void {
    this.aiChatPromptForm.get('content')?.setValue('');

    const sessionId = this.chatSessionService.selectedSessionId();
    if (!sessionId) {
      return;
    }

    const currentInputData = this.chatSessionService.getSessionInputData(sessionId);
    if (!currentInputData) {
      return;
    }

    const updatedInputData: SessionInputData = {
      ...currentInputData,
      text: ''
    };

    this.chatSessionService.setSessionInputData(sessionId, updatedInputData);
  }

  /**
   * Updates the starter conversation model when changed from the AI Models component.
   * @param newModel The new AI model selected for the starter conversation.
   */
  updateStarterConversationModel(newModel: IOpenAiModel): void {
    this.selectedModel = newModel;
  }

  /**
   * Adds a selected agent to the current session's agents array.
   * Prevents duplicate agent IDs from being added.
   * @param agentName The name of the agent to add to the session.
   * @private
   */
  #addAgentToSession(agentName: string): void {
    const session = this.chatSessionService.selectedSession();
    if (!session) return;

    // Find the agent by name in the cached agents list
    const agent = this.#allAgents().find(a => a.name === agentName);
    if (!agent) return;

    // Check if the agent is already in the mention-selected list
    const currentMentionSelected = this.#mentionSelectedAgentIds();
    if (currentMentionSelected.includes(agent.id)) {
      return; // Agent already selected via mention, no need to add again
    }

    // Add to mention-selected agents list
    this.#mentionSelectedAgentIds.set([...currentMentionSelected, agent.id]);

    // Check if the agent is already in the session's agents array
    if (session.agents.includes(agent.id)) {
      return; // Agent already in session, no need to persist again
    }

    // Create updated session with the new agent added
    const updatedSession = {
      ...session,
      agents: [...session.agents, agent.id]
    };

    // Update in the chat sessions array
    this.chatSessionService.chatSessions.update((sessions) => {
      if (!sessions) return sessions;
      return sessions.map((s) => (s.id === session.id ? updatedSession : s));
    });

    // Persist the session to the backend
    this.#apiService.persistSession(updatedSession).subscribe({
      error: (error) => {
        console.error('Failed to add agent to session:', error);
        this.#alertService.showAlert({
          message: `Failed to add agent "${agentName}" to session.`,
          type: AlertEnum.error,
        });
      }
    });
  }

  /**
   * Fetches the list of granted agents from the API based on the session's sensitivity label.
   * This method is called once when the user first types '@' to trigger the mention system.
   * The fetched agents are cached in the #allAgents signal for subsequent filtering.
   * @private
   */
  #fetchGrantedAgents(): void {
    this.#agentsLoading.set(true);

    const session = this.chatSessionService.selectedSession();
    const sensitivityLabel = session?.sensitivityLabels?.[0];

    // Prefer the canonical code (stored thin ref); fall back to a legacy GUID on pre-backfill records.
    this.#apiService.granted(sensitivityLabel?.code ?? sensitivityLabel?.guid).subscribe({
      next: (agents) => {
        this.#allAgents.set(agents);
        this.#allAgentsFetchedForSessionId.set(String(this.chatSessionService.selectedSessionId()));
        this.#agentsLoading.set(false);
      },
      error: (error) => {
        console.error('Failed to fetch granted agents:', error);
        this.#agentsLoading.set(false);
        this.#alertService.showAlert({
          message: 'Failed to load agents for mention system.',
          type: AlertEnum.error,
        });
      }
    });
  }

  /**
   * Scrolls the focused mention item into view within the overlay panel.
   * Ensures keyboard navigation is visible to the user.
   * @private
   */
  #scrollToFocusedItem(): void {
    setTimeout(() => {
      const focusedButton = document.querySelector('.mention-item.focused');
      if (focusedButton) {
        focusedButton.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }, 0);
  }

  /**
   * Safely closes and destroys the mentions suggestion overlay.
   * * * Workflow:
   * 1. **Check Existence**: Verifies if an active `overlayRef` exists to avoid
   * null reference errors.
   * 2. **Memory Management**: Calls the `dispose()` method on the CDK Overlay
   * reference, which removes the element from the DOM and cleans up associated
   * event listeners.
   * 3. **Nullification**: Resets the private `#overlayRef` variable to `null`,
   * ensuring the component state accurately reflects that the menu is closed.
   * * @private
   * @returns {void}
   */
  #closeMentionsOverlay(): void {
    if (this.#overlayRef) {
      this.#overlayRef.dispose();
      this.#overlayRef = null;
      this.#overlayOpenedForSessionId.set(null);
    }
  }

  /**
   * Creates and displays the mentions suggestion overlay when the user types '@' in the textarea.
   *
   * Uses Angular CDK Overlay to position a dropdown panel containing the list of available agents.
   * The overlay is anchored to the textarea element and positioned above it when possible,
   * with a fallback to display below if there's insufficient space above.
   *
   * **Positioning Strategy**:
   * - Primary: Anchors to the top of textarea, overlay extends upward
   * - Fallback: Anchors to bottom of textarea, overlay extends downward
   * - Auto-repositions on scroll via `reposition()` scroll strategy
   *
   * **Lifecycle**:
   * 1. Validates overlay isn't already open and template exists
   * 2. Creates flexible positioning strategy with primary/fallback positions
   * 3. Creates overlay with positioning and scroll strategies
   * 4. Attaches template portal to render the mention list
   *
   * @private
   * @returns {void}
   */
  #showMentionsOverlay(): void {
    // Capture the signal value
    const template = this.mentionListTemplate();

    // Guard against undefined
    if (this.#overlayRef || !template) return;

    this.#overlayOpenedForSessionId.set(String(this.chatSessionService.selectedSessionId()));

    const strategy = this.#overlay.position()
      .flexibleConnectedTo(this.chatInput()!.nativeElement)
      .withPositions([{
        originX: 'start',
        originY: 'top',      // Anchor it to the top of the textarea...
        overlayX: 'start',
        overlayY: 'bottom',   // ...and place the bottom of the menu there
        offsetY: -8          // Pull it 8px above the textarea
      }, {
        // Fallback: if there's no room above, show it below
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top',
        offsetY: 8
      }]);

    this.#overlayRef = this.#overlay.create({
      positionStrategy: strategy,
      scrollStrategy: this.#overlay.scrollStrategies.reposition()
    });

    // 3. Now template is guaranteed to be TemplateRef<any>
    const portal = new TemplatePortal(template, this.#viewContainerRef);
    this.#overlayRef.attach(portal);
  }
}
